import { ref } from "vue";

// The visual states a dialog moves through.
export const State = {
  OPENING: "opening",
  OPEN: "open",
  CLOSING: "closing",
  CLOSED: "closed",
};

// The states each state may transition to. opening↔closing are both allowed so a
// dismiss can interrupt an in-progress open (and vice versa). Any move not listed
// here is rejected, so the dialog can never land in an invalid state.
const ALLOWED_TRANSITIONS = {
  [State.CLOSED]: [State.OPENING],
  [State.OPENING]: [State.OPEN, State.CLOSING],
  [State.OPEN]: [State.CLOSING],
  [State.CLOSING]: [State.CLOSED, State.OPENING],
};

/**
 * The sp-dialog visual state machine: a reactive `state` plus a guarded
 * `transitionTo` that only applies legal moves. Pure state logic, no DOM.
 *
 * @returns {{
 *   state: import("vue").Ref<string>,
 *   transitionTo: (next: string) => boolean,
 * }}
 */
export function useDialogState() {
  const state = ref(State.CLOSED);

  /**
   * Move to `next` only if it is a legal transition from the current state.
   *
   * @param {string} next - The target state; one of the `State` values.
   * @returns {boolean} Whether the move was applied (false when rejected, so
   *   callers can bail).
   */
  function transitionTo(next) {
    if (!ALLOWED_TRANSITIONS[state.value].includes(next)) {
      return false;
    }
    state.value = next;
    return true;
  }

  return { state, transitionTo };
}
